import Checkbox from "@material-ui/core/Checkbox";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles, withStyles } from "@material-ui/styles";
import React, { Dispatch, SetStateAction, useContext } from "react";
import { DARK_ACCENT, WHITE } from "../../../../../constants/colors";
import { ThemeContext } from "../../../../../context";

interface Props {
  value: string;
  setValue: Dispatch<SetStateAction<string>>;
}

// Two separate static styled-components/hooks (one per theme) rather than one driven by a dynamic
// "function of props" color - same reasoning as UsersTable.tsx's identical split (and
// UserSearchField.tsx's identical bug/fix): this field's text/underline/label/icon color needs to
// follow the app's light/dark theme, not stay hardcoded to WHITE (invisible against the light
// theme's own light background). Both are always instantiated (Rules of Hooks), and the component
// picks whichever matches the current theme.
const DarkTextField = withStyles({
  root: {
    "& .MuiInputBase-input": {
      color: WHITE, // Text color
      height: "28px",
    },
    "& .MuiInput-underline:before": {
      borderBottomColor: "#fff8", // Semi-transparent underline
    },
    "& .MuiInput-underline:hover:before": {
      borderBottomColor: WHITE, // Solid underline on hover
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: WHITE, // Solid underline on focus
    },
  },
})(TextField);

const LightTextField = withStyles({
  root: {
    "& .MuiInputBase-input": {
      color: DARK_ACCENT, // Text color
      height: "28px",
    },
    "& .MuiInput-underline:before": {
      borderBottomColor: `${DARK_ACCENT}88`, // Semi-transparent underline
    },
    "& .MuiInput-underline:hover:before": {
      borderBottomColor: DARK_ACCENT, // Solid underline on hover
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: DARK_ACCENT, // Solid underline on focus
    },
  },
})(TextField);

// Defined once at module scope (rather than calling makeStyles({...})() inline on every render)
// - the inline pattern creates a brand new style-sheet hook per render, which under React 18
// crashes on unmount with "Cannot read properties of undefined (reading 'refs')" in
// @material-ui/styles' detach cleanup.
const useDarkStyles = makeStyles({
  input: {
    color: `${WHITE} !important`,
    "&::before": {
      borderBottom: "1px solid #FFF",
    },
    "&::placeholder": {
      color: WHITE,
    },
  },
  popupIndicator: {
    color: WHITE,
  },
});

const useLightStyles = makeStyles({
  input: {
    color: `${DARK_ACCENT} !important`,
    "&::before": {
      borderBottom: `1px solid ${DARK_ACCENT}`,
    },
    "&::placeholder": {
      color: DARK_ACCENT,
    },
  },
  popupIndicator: {
    color: DARK_ACCENT,
  },
});

const AnnotationStatusSearchField = (props: Props) => {
  const { value, setValue } = props;
  const options = ["All", "Approved", "Pending", "Unapproved"];

  const { theme } = useContext(ThemeContext);
  const darkClasses = useDarkStyles();
  const lightClasses = useLightStyles();
  const classes = theme === "dark" ? darkClasses : lightClasses;
  const labelColor = theme === "dark" ? WHITE : DARK_ACCENT;
  const StyledTextField = theme === "dark" ? DarkTextField : LightTextField;

  return (
    <Autocomplete
      value={value}
      options={options}
      defaultValue={"All"}
      disableClearable
      onChange={(event, value) => setValue(value)}
      renderOption={(option, { selected }) => {
        return (
          <>
            <Checkbox style={{ marginRight: 8 }} checked={selected} color="primary" />
            {option}
          </>
        );
      }}
      classes={{
        input: classes.input,
        popupIndicator: classes.popupIndicator,
      }}
      renderInput={(params) => <StyledTextField {...params} label="Status" InputLabelProps={{ style: { color: labelColor } }} />}
    />
  );
};

export default AnnotationStatusSearchField;
